import type { PhoneCountry } from "./types";

export const isValidPhonePrefixForTyping = (
  value: string,
  country: PhoneCountry | null
): boolean => {
  if (!value) return true;

  const prefixes = (country?.prefixes || [])
    .map((prefix) => `${prefix || ""}`.trim())
    .filter(Boolean);

  if (!prefixes.length) return true;

  return prefixes.some(
    (prefix) =>
      prefix.startsWith(value) || value.startsWith(prefix)
  );
};

export const getPhoneRegexByCountry = (
  country: PhoneCountry | null
): RegExp => {
  const min = Number(country?.min) || 10;
  const max = Number(country?.max) || min;

  const prefixes = (country?.prefixes || [])
    .map((prefix) => `${prefix || ""}`.trim())
    .filter(Boolean);

  if (!prefixes.length) {
    return new RegExp(`^\\d{${min},${max}}$`);
  }

  const regexParts = prefixes
    .map((prefix) => {
      const minRemainingLength = Math.max(
        0,
        min - prefix.length
      );

      const maxRemainingLength = Math.max(
        0,
        max - prefix.length
      );

      if (maxRemainingLength < minRemainingLength) {
        return "";
      }

      return `${prefix}\\d{${minRemainingLength},${maxRemainingLength}}`;
    })
    .filter(Boolean);

  if (!regexParts.length) {
    return /^\d{10}$/;
  }

  return new RegExp(
    `^(?:${regexParts.join("|")})$`
  );
};

export const isValidPhoneNumberForCountry = (
  value: string,
  country: PhoneCountry | null
): boolean => {
  if (!value) return false;

  return getPhoneRegexByCountry(country).test(value);
};

export const isValidPhoneNumberByCountryCode = (
  phone: unknown,
  phoneCountries: PhoneCountry[] = [],
  phoneCountryCode?: string
): boolean => {
  const phoneStr = `${phone || ""}`.replace(/\D/g, "");

  if (!phoneStr) return false;

  const selectedCountry = phoneCountries.find(
    (country) => country?.code === phoneCountryCode
  );

  if (!selectedCountry) {
    return phoneStr.length === 10;
  }

  return isValidPhoneNumberForCountry(
    phoneStr,
    selectedCountry
  );
};